import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  bumpJobChatListItem,
  normalizeJobChatMessage,
  upsertMessageInPages,
} from "../actions/jobChats.actions";
import { jobChatsQueryKeys } from "../jobChats.queryKeys";

/**
 * Listens for realtime messages of a single open job-event chat and prepends them
 * to the messages cache (no refetch). `onNewMessage` runs for every accepted message.
 */
export const useJobChatSocket = (socket, jobEventUid, companyId, onNewMessage, onStatusChange, onRead) => {
  const queryClient = useQueryClient();
  const processedMessageUidsRef = useRef(new Set());

  useEffect(() => {
    if (!socket || !jobEventUid || !companyId) return;

    const handleNewMessage = ({ jobEventUid: incomingUid, message }) => {
      if (incomingUid !== jobEventUid || !message) return;
      if (message.companyId != null && Number(message.companyId) !== Number(companyId)) return;

      const messageUid = message.uid;
      if (processedMessageUidsRef.current.has(messageUid)) return;
      processedMessageUidsRef.current.add(messageUid);

      const normalized = normalizeJobChatMessage(message);

      queryClient.setQueryData(jobChatsQueryKeys.messages(companyId, jobEventUid), (oldData) =>
        upsertMessageInPages(oldData, normalized)
      );
      onNewMessage?.(normalized);
    };

    const handleStatus = ({ jobEventUid: incomingUid, status }) => {
      if (incomingUid !== jobEventUid) return;
      onStatusChange?.(status);
    };

    const handleRead = ({ jobEventUid: incomingUid, reader, lastReadAt }) => {
      if (incomingUid !== jobEventUid) return;
      onRead?.(reader, lastReadAt);
    };

    socket.on("jobChatMessage", handleNewMessage);
    socket.on("jobChatStatus", handleStatus);
    socket.on("jobChatRead", handleRead);

    return () => {
      socket.off("jobChatMessage", handleNewMessage);
      socket.off("jobChatStatus", handleStatus);
      socket.off("jobChatRead", handleRead);
    };
  }, [socket, jobEventUid, companyId, queryClient, onNewMessage, onStatusChange, onRead]);
};

/**
 * Keeps the contact-center conversation list fresh in real time by patching the
 * cache in place for the **active company only**.
 */
export const useJobChatsListSocket = (socket, activeCompanyId, openJobEventUid = null) => {
  const queryClient = useQueryClient();
  const { uid, workforces } = useSelector((state) => state.userState);

  const identityRef = useRef({ uid, workforceUids: [] });
  identityRef.current = {
    uid,
    workforceUids: (workforces || []).map((w) => w.uid).filter(Boolean),
  };
  const openRef = useRef(openJobEventUid);
  openRef.current = openJobEventUid;
  const companyRef = useRef(activeCompanyId);
  companyRef.current = activeCompanyId;

  useEffect(() => {
    if (!socket || !activeCompanyId) return;

    const isFromMe = (sender) => {
      if (!sender) return false;
      const { uid: myUid, workforceUids } = identityRef.current;
      if (sender.type === "user" && sender.uid === myUid) return true;
      if (sender.type === "workforce" && sender.uid && workforceUids.includes(sender.uid)) return true;
      return false;
    };

    const handleMessage = ({ jobEventUid, message }) => {
      if (!jobEventUid || !message) return;
      if (message.companyId != null && Number(message.companyId) !== Number(companyRef.current)) return;

      const preview = message.content?.text ?? message.content?.name ?? null;
      const lastMessageAt = message.createdAt ?? new Date().toISOString();
      const incrementUnread = jobEventUid !== openRef.current && !isFromMe(message.sender);

      queryClient.setQueryData(jobChatsQueryKeys.active(companyRef.current), (old) =>
        bumpJobChatListItem(old, jobEventUid, { lastMessage: preview, lastMessageAt, incrementUnread })
      );
      queryClient.setQueryData(jobChatsQueryKeys.history(companyRef.current), (old) =>
        bumpJobChatListItem(old, jobEventUid, { lastMessage: preview, lastMessageAt, incrementUnread })
      );
    };

    const handleStatus = () => {
      queryClient.invalidateQueries({ queryKey: jobChatsQueryKeys.active(companyRef.current) });
      queryClient.invalidateQueries({ queryKey: jobChatsQueryKeys.history(companyRef.current) });
    };

    socket.on("jobChatMessage", handleMessage);
    socket.on("jobChatStatus", handleStatus);

    return () => {
      socket.off("jobChatMessage", handleMessage);
      socket.off("jobChatStatus", handleStatus);
    };
  }, [socket, activeCompanyId, queryClient]);
};
